import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Syncs a tab/section state with URL search params so the browser back button works.
 * Each tab change pushes a history entry. Back button restores the previous tab.
 *
 * @param {string} paramName  - URL search param name (e.g., "section", "tab")
 * @param {string} defaultTab - Default tab when param is absent
 * @returns {[string, (tab: string) => void]}
 */
export function useTabNav(paramName = "tab", defaultTab = "overview") {
  const location = useLocation();
  const navigate = useNavigate();

  const currentTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get(paramName) || defaultTab;
  }, [location.search, paramName, defaultTab]);

  const setTab = useCallback((newTab) => {
    const params = new URLSearchParams(location.search);
    if (newTab === defaultTab) {
      params.delete(paramName);
    } else {
      params.set(paramName, newTab);
    }
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" });
  }, [location.search, location.pathname, navigate, paramName, defaultTab]);

  return [currentTab, setTab];
}
